import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useAuth } from '../../context/AuthContext'
import { useRecipe, RecipeData } from '../../context/RecipeContext'
import styles from './RecipeView.module.css'
import { Notification } from '../../components/Notification'
import ConfirmationDialog from '../../components/ConfirmationDialog'
import IngredientDietaryAttributes from '../../components/IngredientDietaryAttributes/IngredientDietaryAttributes'
import RecipeDietaryAttributes from '../../components/RecipeDietaryAttributes/RecipeDietaryAttributes'
import { getAllRelevantTags } from '../../utils/dietaryComparisonUtils'

const RecipeView: React.FC = () => {
  const { id, variantId } = useParams<{ id?: string; variantId?: string }>()
  const navigate = useNavigate()
  const { user, logout, getUserPreferences } = useAuth()
  
  const handleLogin = () => {
    // TODO: Navigate to login page or show login modal
  };
  const { getRecipeById, getVariant, save, unsave, savedRecipes, createGroceryList, addRecipeToGroceryList, getGroceryListWithItems, getGroceryList } = useRecipe()

  const [recipe, setRecipe] = useState<RecipeData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [scale, setScale] = useState<number>(1)
  const [notification, setNotification] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [showLeaveSiteDialog, setShowLeaveSiteDialog] = useState(false)
  const [completedIngredients, setCompletedIngredients] = useState<Set<string>>(new Set())
  const [completedInstructions, setCompletedInstructions] = useState<Set<string>>(new Set())
  const [dietaryInfo, setDietaryInfo] = useState<any>(null)
  const [userPreferences, setUserPreferences] = useState<any>(null)



  // Load user preferences when user logs in
  useEffect(() => {
    if (user) {
      getUserPreferences().then(prefs => {
        if (prefs) {
          setUserPreferences(prefs);
        }
      });
    }
  }, [user, getUserPreferences]);

  useEffect(() => {
    if (!id && !variantId) return
    ;(async () => {
      setLoading(true)
      setError(null)
      
      let data: RecipeData | null = null
      
      if (variantId) {
        // This is a recipe variant
        const variant = await getVariant(variantId)
        
        if (variant) {
          // Convert variant to RecipeData format
          data = {
            id: variant.id,
            title: variant.title,
            token_count: 0, // Will be calculated
            ingredients: variant.variant_ingredients.map(ing => ({
              id: ing.id || '',
              name: ing.name,
              quantity: ing.quantity,
              unit: ing.unit,
              order: ing.order
            })),
            instructions: variant.variant_instructions.map(ins => ({
              id: ins.id || '',
              text: ins.text,
              order: ins.order
            })),
            created_at: variant.created_at,
            type: 'variant',
            base_recipe_id: variant.base_recipe_id,
            base_recipe_title: variant.base_recipe_title
          }
        }
      } else if (id) {
        // This is a regular recipe
        data = await getRecipeById(id)
      }
      
      if (!data) {
        setError('Recipe not found')
      } else {
        setRecipe(data)
        // Dietary info is now included in the recipe data
        setDietaryInfo(data.dietary_info)
      }
      setLoading(false)
    })()
  }, [id, variantId, getRecipeById, getVariant])

  const isSaved = useMemo(() => {
    if (!recipe) return false
    return Array.isArray(savedRecipes) && savedRecipes.some(r => r.id === recipe.id)
  }, [recipe, savedRecipes])

  const heroSrc = useMemo(() => {
    if (!recipe) return null
    const primary = recipe.photos?.find(p => p.is_primary) || recipe.photos?.[0]
    return primary?.url || recipe.image || null
  }, [recipe])

  const parseQuantity = (q: string | null | undefined): number | null => {
    if (!q) return null
    const s = String(q).trim()
    let m = s.match(/^(\d+)\s+(\d+)\/(\d+)/)
    if (m) { return parseInt(m[1],10) + parseInt(m[2],10) / (parseInt(m[3],10)||1) }
    m = s.match(/^(\d+)\/(\d+)/)
    if (m) { return parseInt(m[1],10) / (parseInt(m[2],10)||1) }
    m = s.match(/^(\d+(?:\.\d+)?)/)
    if (m) return parseFloat(m[1])
    return null
  }

  const formatQuantity = (value: number): string => {
    const eighths = Math.round(value * 8)
    const snapped = eighths / 8
    const whole = Math.floor(snapped)
    const frac = snapped - whole
    const denOptions = [2, 3, 4, 8]
    let bestNum = 0, bestDen = 1, bestErr = Infinity
    for (const den of denOptions) {
      const num = Math.round(frac * den)
      const err = Math.abs(frac - num/den)
      if (err < bestErr) { bestErr = err; bestNum = num; bestDen = den }
    }
    if (whole === 0 && bestNum === 0) return '0'
    if (bestNum === 0) return String(whole)
    if (whole === 0) return `${bestNum}/${bestDen}`
    return `${whole} ${bestNum}/${bestDen}`
  }

  const scaledIngredients = useMemo(() => {
    if (!recipe) return []
    if (scale === 1) return recipe.ingredients
    
    return recipe.ingredients.map(ing => {
      const base = parseQuantity(ing.quantity)
      const nextQty = base != null ? formatQuantity(base * scale) : ing.quantity
      return { ...ing, quantity: nextQty }
    })
  }, [recipe, scale])

  const addToGroceryList = async () => {
    if (!recipe || !user) return
    
    try {
      // Get or create a grocery list
      let groceryList = localStorage.getItem('currentGroceryList')
      let listId: string
      
      if (groceryList) {
        try {
          const parsed = JSON.parse(groceryList)
          listId = parsed.id
          
          // Verify the grocery list still exists in the database
          const existingList = await getGroceryList(listId)
          if (!existingList) {
            // List no longer exists, clear localStorage and create a new one
            localStorage.removeItem('currentGroceryList')
            const newList = await createGroceryList('My Grocery List')
            if (!newList) {
              setNotification({
                type: 'error',
                message: 'Failed to create grocery list'
              })
              return
            }
            listId = newList.id
          }
        } catch (parseError) {
          // Invalid localStorage data, clear it and create a new list
          localStorage.removeItem('currentGroceryList')
          const newList = await createGroceryList('My Grocery List')
          if (!newList) {
            setNotification({
              type: 'error',
              message: 'Failed to create grocery list'
            })
            return
          }
          listId = newList.id
        }
      } else {
        // Create a new grocery list
        const newList = await createGroceryList('My Grocery List')
        if (!newList) {
          setNotification({
            type: 'error',
            message: 'Failed to create grocery list'
          })
          return
        }
        listId = newList.id
      }
      
      await addRecipeToGroceryList(listId, recipe.id, scale)
      
      // Get the updated grocery list with normalized items
      const updatedList = await getGroceryListWithItems(listId)
      if (!updatedList) {
        setNotification({
          type: 'error',
          message: 'Failed to get updated grocery list'
        })
        return
      }
      
      // Save the normalized grocery list to localStorage
      localStorage.setItem('currentGroceryList', JSON.stringify(updatedList))
      
      // Show success notification
      setNotification({
        type: 'success',
        message: `Added ${recipe.title} to your grocery list with ${updatedList.items.length} normalized items!`
      })
      
    } catch (error) {
      console.error('Error adding to grocery list:', error)
      setNotification({
        type: 'error',
        message: 'Failed to add recipe to grocery list. Please try again.'
      })
    }
  }

  const handleViewGroceryList = () => {
    navigate('/grocery-list')
  }

  const handleStayHere = () => {
    // Just close the notification, stay on current page
  }

  const handleCloseNotification = () => {
    setNotification(null)
  }

  const handleSourceClick = (sourceUrl: string) => {
    setShowLeaveSiteDialog(true)
  }

  const handleLeaveConfirm = () => {
    setShowLeaveSiteDialog(false)
    if (recipe?.source_url) {
      window.open(recipe.source_url, '_blank', 'noopener,noreferrer')
    }
  }

  const handleLeaveCancel = () => {
    setShowLeaveSiteDialog(false)
  }

  const toggleIngredient = (index: number) => {
    setCompletedIngredients(prev => {
      const newSet = new Set(prev)
      const key = index.toString()
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  const toggleInstruction = (index: number) => {
    setCompletedInstructions(prev => {
      const newSet = new Set(prev)
      const key = index.toString()
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  if (loading) {
    return (
      <div className={styles.container}><Header user={user} onLogin={handleLogin} onLogout={logout} /><div className={styles.content}>Loading…</div></div>
    )
  }
  if (error || !recipe) {
    return (
      <div className={styles.container}><Header user={user} onLogin={handleLogin} onLogout={logout} /><div className={styles.content}>{error || 'Recipe not found'}</div></div>
    )
  }

  return (
    <div className={styles.container}>
      <Header user={user} onLogin={handleLogin} onLogout={logout} />
      <div className={styles.hero}>{heroSrc ? <img src={heroSrc} alt={recipe.title} /> : <div className={styles.heroPlaceholder}>No image yet</div>}</div>
      <div className={styles.content}>
        <div className={styles.headerRow}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{recipe.title}</h1>
            {recipe.dietary_info && (
              <RecipeDietaryAttributes
                dietaryAttributes={recipe.dietary_info}
                userPreferences={userPreferences}
                variant="default"
                showOnlyMatches={true}
                maxTags={6}
              />
            )}
          </div>
          <div className={styles.scaleRow}>
            <label htmlFor="scaleSelect" className={styles.scaleLabel}>Scale:</label>
            <select id="scaleSelect" className={styles.scaleSelect} value={scale} onChange={(e)=> setScale(parseFloat(e.target.value))}>
              <option value={0.5}>1/2x</option>
              <option value={1}>1x</option>
              <option value={2}>2x</option>
            </select>
          </div>
        </div>
        
        {/* Source URL */}
        {recipe.source_url && (
          <div className={styles.sourceUrl}>
            <span className={styles.sourceLabel}>Source:</span>
            <a 
              href={recipe.source_url}
              className={styles.sourceLink}
              onClick={(e) => { e.preventDefault(); handleSourceClick(recipe.source_url!) }}
              title="Visit original recipe source"
              rel="noopener noreferrer"
            >
              {new URL(recipe.source_url).hostname}
            </a>
          </div>
        )}
        <div className={styles.actions}>
          <button className={styles.button} onClick={() => { isSaved ? unsave(recipe.id) : save(recipe.id) }}>{isSaved ? 'Unsave' : 'Save'}</button>
          {user && (
            <button className={styles.button} onClick={() => navigate(`/recipes/${recipe.id}/customize`, { state: { recipe } })}>{recipe.type === 'variant' ? 'Edit' : 'Customize'}</button>
          )}
          <button className={styles.button} onClick={() => addToGroceryList()}>+ Grocery List</button>
        </div>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Ingredients</h2>
          <ul className={styles.list}>
            {scaledIngredients.map((item, idx) => {
              // Get the original ingredient with dietary info
              const originalIngredient = recipe.ingredients[idx] as any;
              
              // Get all relevant tags (dietary + health concerns)
              const { dietaryMatches, healthConflicts, healthMatches } = getAllRelevantTags(
                originalIngredient?.dietary_info,
                userPreferences,
                originalIngredient?.dietary_info?.nutritional_profile,
                user?.preferences?.healthConcerns?.map(hc => ({
                  name: hc.health_concern.name,
                  type: 'min' as const,
                  value: 0
                })) || []
              );
              
              return (
                <li 
                  key={idx} 
                  className={`${styles.li} ${styles.clickableItem} ${completedIngredients.has(idx.toString()) ? styles.completed : ''}`}
                  onClick={() => toggleIngredient(idx)}
                >
                  <div className={styles.ingredientContent}>
                    <span className={styles.ingredientText}>
                      {item.quantity ?? ''} {item.unit ?? ''} {item.name}
                    </span>
                    {originalIngredient?.dietary_info && (
                      <IngredientDietaryAttributes
                        ingredient={{
                          name: item.name,
                          dietaryAttributes: originalIngredient.dietary_info,
                          healthConcerns: (originalIngredient as any).health_concerns || [],
                          conflicts: healthConflicts,
                          matches: [...healthMatches, ...dietaryMatches]
                        }}
                        userPreferences={userPreferences}
                        userHealthConcerns={user?.preferences?.healthConcerns?.map(hc => ({
                          name: hc.health_concern.name,
                          type: 'min' as const,
                          value: 0
                        })) || []}
                        variant="inline"
                        showOnlyMatches={true}
                        showConflicts={true}
                        showHealthConcerns={false}
                        maxTags={3}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Instructions</h2>
          <ol className={styles.listOrdered}>
            {recipe.instructions
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((step, idx) => (
                <li 
                  key={step.order} 
                  className={`${styles.li} ${styles.clickableItem} ${completedInstructions.has(idx.toString()) ? styles.completed : ''}`}
                  onClick={() => toggleInstruction(idx)}
                >
                  {step.text}
                </li>
              ))}
          </ol>
        </section>
      </div>
      
      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onViewGroceryList={notification.type === 'success' ? handleViewGroceryList : undefined}
          onStayHere={handleStayHere}
          onClose={handleCloseNotification}
          autoDismiss={true}
          autoDismissDelay={6000}
        />
      )}

      {/* Leave Site Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showLeaveSiteDialog}
        title="You are leaving PlatefullAI"
        message="You are about to open the original recipe source in a new tab. Continue?"
        confirmText="Open Source"
        cancelText="Stay"
        onConfirm={handleLeaveConfirm}
        onCancel={handleLeaveCancel}
        type="danger"
      />
    </div>
  )
}

export default RecipeView


